import {
  Badge,
  Box,
  HStack,
  Stack,
  Text,
  type StackProps,
} from "@chakra-ui/react";
import { LuAlertCircle } from "react-icons/lu";
import type { SimilarReason } from "@/core/common/hooks/useSimilarRecords";

export interface SimilarRecordItem {
  id: string;
  title: string;
  subtitle?: string;
  reasons: SimilarReason[];
  enabled?: boolean;
}

interface SimilarRecordsHintProps extends Omit<StackProps, "children"> {
  items: SimilarRecordItem[];
  /** Plural noun, e.g. "solicitantes". */
  entityLabel: string;
  max?: number;
}

const REASON_LABELS: Record<SimilarReason, string> = {
  name: "Nombre parecido",
  email: "Mismo correo",
  phone: "Mismo teléfono",
  code: "Mismo código",
};

const isStrong = (reasons: SimilarReason[]) =>
  reasons.some((reason) => reason !== "name");

/** Non-blocking warning listing existing records that look like duplicates. */
export const SimilarRecordsHint = ({
  items,
  entityLabel,
  max = 4,
  ...rest
}: SimilarRecordsHintProps) => {
  if (!items.length) return null;

  const strong = items.some((item) => isStrong(item.reasons));
  const tone = strong ? "red" : "yellow";
  const hidden = items.length - max;

  return (
    <HStack
      role="status"
      aria-live="polite"
      align="flex-start"
      gap={2}
      px={3}
      py={2}
      rounded="md"
      bg={`${tone}.subtle`}
      fontSize="xs"
      {...rest}
    >
      <Box color={`${tone}.fg`} flexShrink={0} mt="1px" display="inline-flex">
        <LuAlertCircle size={14} />
      </Box>
      <Stack gap={1} minW={0} flex={1}>
        <Text fontWeight="medium" lineHeight="1.45">
          {strong
            ? "Ya existe un registro con estos datos:"
            : `¿Ya existe? Hay ${entityLabel} con nombre similar:`}
        </Text>
        {items.slice(0, max).map((item) => (
          <HStack key={item.id} gap={2} flexWrap="wrap" minW={0}>
            <Text fontWeight="semibold" truncate>
              {item.title}
            </Text>
            {item.subtitle && (
              <Text color="fg.muted" truncate>
                {item.subtitle}
              </Text>
            )}
            {item.reasons
              .filter((reason) => reason !== "name")
              .map((reason) => (
                <Badge key={reason} size="sm" colorPalette="red">
                  {REASON_LABELS[reason]}
                </Badge>
              ))}
            {item.enabled === false && (
              <Badge size="sm" colorPalette="gray">
                Deshabilitado
              </Badge>
            )}
          </HStack>
        ))}
        {hidden > 0 && (
          <Text color="fg.muted">
            y {hidden} más. Búscalos en la tabla de {entityLabel}.
          </Text>
        )}
      </Stack>
    </HStack>
  );
};
